import {
  animate,
  AnimateSharedLayout,
  motion,
  MotionValue,
  useMotionTemplate,
  useMotionValue,
} from "framer-motion";
import { useAtom } from "jotai";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { NameTag } from "../av/Cursor";
import {
  BOT_NAME,
  DEFAULT_CURSOR_SIZE,
  DEFAULT_SPRING,
  ROOM_SERVICE_PRESENCE_KEYS,
} from "../consts";
import { currentUserAtom, useScrollRef } from "../jotai/atoms";
import { useUserInfos } from "../roomservice/maps";
import { usePresenceCallback } from "../roomservice/utils";
import { styled } from "../stitches.config";
import { User, Point2D } from "../types";

export default function CursorIndicators() {
  const userInfos = useUserInfos();
  const [currentUser] = useAtom(currentUserAtom);
  const currentUserScrollX = useMotionValue(0);
  const currentUserScrollY = useMotionValue(0);
  const currentUserScroll = useMotionTemplate`{"x":${currentUserScrollX}, "y":${currentUserScrollY}}`;

  // Control scroll with framer-motion because window.scrollTo() smooth doesn't work in Safari
  useEffect(() => {
    const unsub = currentUserScroll.onChange((scrollString) => {
      const scroll: Point2D = JSON.parse(scrollString);
      window.scrollTo(scroll.x, scroll.y);
    });
    return () => {
      unsub();
    };
  }, [currentUserScroll]);

  return (
    <Container>
      {Object.values(userInfos).map(
        (userInfo) =>
          userInfo.id !== currentUser?.id && (
            <CursorIndicator
              key={userInfo.id}
              info={userInfo}
              scrollX={currentUserScrollX}
              scrollY={currentUserScrollY}
            />
          )
      )}
    </Container>
  );
}

function CursorIndicator({
  info,
  scrollX,
  scrollY,
}: {
  info: User;
  scrollX: MotionValue;
  scrollY: MotionValue;
}) {
  const trueX = useRef(0);
  const trueY = useRef(0);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [visible, setVisible] = useState(false);
  const [right, setRight] = useState(false);
  const [bottom, setBottom] = useState(false);
  const scroll = useScrollRef();

  const onPositionPresence = useCallback(
    (msg: Record<string, Point2D>) => {
      if (!msg[info.id] || !scroll?.current) {
        return;
      }

      const w = window.innerWidth;
      const h = window.innerHeight;
      const minX = scroll.current.x;
      const minY = scroll.current.y;
      const maxX = w + minX;
      const maxY = h + minY;

      // offsetting by canvas padding
      const newX = msg[info.id].x;
      const newY = msg[info.id].y;

      trueX.current = newX;
      trueY.current = newY;

      const delayPadding = 120; // Give some space before we show cursor to prevent the two name tags being shown at once
      const offscreen =
        newX > maxX + delayPadding ||
        newY > maxY + delayPadding ||
        newX < minX - delayPadding - DEFAULT_CURSOR_SIZE ||
        newY < minY - delayPadding - DEFAULT_CURSOR_SIZE;
      setVisible(offscreen);

      // clamp and map to screen
      const screenX = clamp(newX - minX, 0, w);
      const screenY = clamp(newY - minY, 0, h);
      x.set(screenX);
      y.set(screenY);

      setRight(screenX > w / 2);
      setBottom(screenY > h / 2);
    },
    [x, y, info.id, setRight, setBottom, trueX, trueY, scroll]
  );

  usePresenceCallback(ROOM_SERVICE_PRESENCE_KEYS.POSITION, onPositionPresence);

  const jumpToUser = useCallback(() => {
    if (!scroll?.current) return;
    const sourceX = scroll.current.x;
    const sourceY = scroll.current.y;
    const targetX = trueX.current - window.innerWidth / 2;
    const targetY = trueY.current - window.innerHeight / 2;
    scrollX.set(sourceX);
    scrollY.set(sourceY);
    const duration = convertRange(
      Math.sqrt(
        Math.pow(targetX - sourceX, 2) + Math.pow(targetY - sourceY, 2)
      ),
      [1600, 8000], // roughly diameter of furthest/shortest distances to scroll
      [0.3, 3]
    );
    animate(scrollX, targetX, { duration });
    animate(scrollY, targetY, { duration });
  }, [trueX, trueY, scrollX, scrollY, scroll]);

  if (info.name === BOT_NAME) return null;
  return (
    <>
      <Cursor
        style={{ x, y }}
        animate={{ opacity: visible ? 1 : 0 }}
        transition={{ duration: 0.15 }}
      >
        <AnimateSharedLayout>
          <NameTagWrapper
            layout
            layoutId={info.id}
            style={{
              right: right ? 0 : undefined,
              bottom: bottom ? 0 : undefined,
            }}
            transition={DEFAULT_SPRING}
            onClick={() => jumpToUser()}
          >
            <Name
              style={{
                backgroundColor: info.color,
              }}
            >
              {info.name}
            </Name>
          </NameTagWrapper>
        </AnimateSharedLayout>
      </Cursor>
    </>
  );
}

const Container = styled("ul", {
  position: "fixed",
  pointerEvents: "none",
  top: 0,
  left: 0,
  width: 0,
  height: 0,
});

const NameTagWrapper = styled(motion.div, {
  position: "absolute",
  padding: "$2",
  cursor: "pointer",
});

const Cursor = styled(motion.li, {
  pointerEvents: "all",
  position: "absolute",
});

const Name = styled(NameTag, {
  position: "relative",
});

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(v, min));
}
function convertRange(value: number, r1: number[], r2: number[]) {
  return ((value - r1[0]) * (r2[1] - r2[0])) / (r1[1] - r1[0]) + r2[0];
}
